import {
    ActionIcon,
    Button,
    Card,
    Fieldset,
    Group,
    MultiSelect,
    NumberInput,
    Select,
    Stack,
    Switch,
    Text,
    Tooltip,
} from '@mantine/core';
import { type UseFormReturnType } from '@mantine/form';
import { IconInfoCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { useBankCodeOptions } from '~/entities/bank';
import { currencies, Currency } from '~/entities/currency';
import { markets } from '~/entities/market';
import { paymentMethods } from '~/entities/paymentMehtod';
import {
    PayoutPaymentMethod,
    PayoutPaymentType,
    type PayoutPaymentChannel,
    type PayoutTraderConfig,
} from '~/entities/payout';
import { requisiteTypes } from '~/entities/requisiteType';
import { type AcceptRateLimitMode } from '../../../../model';
import { BankCodeSelectOption } from './components';

interface ConfigEditorProps<T extends { acceptRateLimitMode: AcceptRateLimitMode; config?: PayoutTraderConfig }> {
    form: UseFormReturnType<T, unknown>;
    isLoading?: boolean;
}

const banksTooltip =
    'Укажите список банков для разрешения и/или запрета. Если список разрешённых пуст — доступны все банки, кроме указанных в запрещённых. Если список разрешённых заполнен — доступны только банки из него, за исключением банков из запрещённого списка. Запрещённые банки всегда имеют приоритет.';

const acceptRateLimitModes: Array<{ label: string; value: AcceptRateLimitMode }> = [
    { value: 'default', label: 'По умолчанию' },
    { value: 'unlimited', label: 'Без лимита' },
    { value: 'custom', label: 'Персональный лимит' },
];

export const ConfigEditor = <T extends { acceptRateLimitMode: AcceptRateLimitMode; config?: PayoutTraderConfig }>({
    form,
    isLoading,
}: ConfigEditorProps<T>) => {
    const bankOptions = useBankCodeOptions();
    const canSplitOrder = form.values.config?.canSplitOrder === true;
    const useAllDayLimit = form.values.config?.limits?.useAllDay === true;
    const useCustomAcceptRateLimit = form.values.acceptRateLimitMode === 'custom';

    const addPaymentChannel = () => {
        const value = {
            config: {
                maxQueueLength: '',
            },
            filter: {
                amount: {
                    min: { value: '' },
                    max: { value: '' },
                },
                bankCodes: {
                    allowed: [],
                    forbidden: [],
                },
                currency: Currency.CURRENCY_UNSPECIFIED,
                method: PayoutPaymentMethod.METHOD_UNSPECIFIED,
                type: PayoutPaymentType.TYPE_UNSPECIFIED,
            },
        } satisfies PayoutPaymentChannel;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form.insertListItem('config.paymentChannels', value as any);
    };

    const removePaymentChannel = (index: number) => {
        form.removeListItem('config.paymentChannels', index);
    };

    return (
        <Fieldset legend='Конфигурация'>
            <Stack gap={16}>
                <Stack gap={4}>
                    <Switch
                        w='100%'
                        label='Единый лимит на весь день'
                        {...form.getInputProps('config.limits.useAllDay', { type: 'checkbox' })}
                    />

                    {useAllDayLimit ? (
                        <NumberInput
                            label='Лимит на весь день'
                            placeholder='Введите число'
                            valueIsNumericString
                            min={0}
                            {...form.getInputProps('config.limits.allDay.value')}
                        />
                    ) : (
                        <Group grow>
                            <NumberInput
                                label='Дневной лимит'
                                placeholder='Введите число'
                                valueIsNumericString
                                min={0}
                                {...form.getInputProps('config.limits.day.value')}
                            />
                            <NumberInput
                                label='Ночной лимит'
                                placeholder='Введите число'
                                valueIsNumericString
                                min={0}
                                {...form.getInputProps('config.limits.night.value')}
                            />
                        </Group>
                    )}
                </Stack>

                <Stack gap={4}>
                    <Select
                        label='Антифрод-лимит взятия заявок'
                        data={acceptRateLimitModes}
                        allowDeselect={false}
                        {...form.getInputProps('acceptRateLimitMode')}
                    />

                    {useCustomAcceptRateLimit && (
                        <Group grow>
                            <NumberInput
                                label='Макс. заявок в окне'
                                placeholder='Введите число'
                                valueIsNumericString
                                allowDecimal={false}
                                min={1}
                                {...form.getInputProps('config.acceptRateLimit.maxAccepts')}
                            />
                            <NumberInput
                                label='Окно лимита, сек'
                                placeholder='Введите число'
                                valueIsNumericString
                                allowDecimal={false}
                                min={1}
                                {...form.getInputProps('config.acceptRateLimit.windowSeconds')}
                            />
                        </Group>
                    )}
                </Stack>

                <MultiSelect
                    searchable
                    label='Обязательная работа через площадки'
                    placeholder='Выберите из списка'
                    data={markets}
                    {...form.getInputProps('config.allowedMarkets')}
                />

                <Stack gap={4}>
                    <Switch
                        w='100%'
                        label='Доступ к дроблению ордера'
                        {...form.getInputProps('config.canSplitOrder', { type: 'checkbox' })}
                    />

                    {canSplitOrder && (
                        <NumberInput
                            label='Минимальная сумма дочернего ордера'
                            placeholder='Введите число'
                            valueIsNumericString
                            min={0}
                            {...form.getInputProps('config.minSplitChildAmount.value')}
                        />
                    )}
                </Stack>

                {form.values.config?.paymentChannels?.map((_, index) => (
                    <Card
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        padding='md'
                        withBorder
                        radius='md'
                    >
                        <Group
                            justify='space-between'
                            mb='md'
                        >
                            <Text fw={500}>Канал #{index + 1}</Text>

                            <ActionIcon
                                size='lg'
                                color='red'
                                variant='subtle'
                                onClick={() => removePaymentChannel(index)}
                                aria-label='Удалить канал'
                            >
                                <IconTrash />
                            </ActionIcon>
                        </Group>

                        <Stack gap='xs'>
                            <Group grow>
                                <Select
                                    label='Валюта'
                                    data={currencies}
                                    placeholder='Выберите валюту'
                                    searchable
                                    {...form.getInputProps(`config.paymentChannels.${index}.filter.currency`)}
                                    clearable
                                />
                                <Select
                                    label='Метод'
                                    data={paymentMethods}
                                    placeholder='Выберите метод'
                                    {...form.getInputProps(`config.paymentChannels.${index}.filter.method`)}
                                    clearable
                                />
                                <Select
                                    label='Тип реквизита'
                                    placeholder='Выберите тип'
                                    data={requisiteTypes}
                                    {...form.getInputProps(`config.paymentChannels.${index}.filter.type`)}
                                    clearable
                                />
                            </Group>

                            <Group
                                align='center'
                                gap='xs'
                            >
                                <Group
                                    grow
                                    flex={1}
                                    align='center'
                                >
                                    <MultiSelect
                                        label='Разрешенные банки'
                                        data={bankOptions}
                                        placeholder='Выберите банк'
                                        renderOption={({ option, checked }) => (
                                            <BankCodeSelectOption
                                                bankCode={option.value}
                                                checked={checked}
                                            />
                                        )}
                                        {...form.getInputProps(
                                            `config.paymentChannels.${index}.filter.bankCodes.allowed`,
                                        )}
                                        clearable
                                        searchable
                                    />

                                    <MultiSelect
                                        label='Запрещённые банки'
                                        data={bankOptions}
                                        placeholder='Выберите банк'
                                        renderOption={({ option, checked }) => (
                                            <BankCodeSelectOption
                                                bankCode={option.value}
                                                checked={checked}
                                            />
                                        )}
                                        {...form.getInputProps(
                                            `config.paymentChannels.${index}.filter.bankCodes.forbidden`,
                                        )}
                                        clearable
                                        searchable
                                    />
                                </Group>

                                <div>
                                    <Tooltip
                                        label={banksTooltip}
                                        maw={280}
                                        position='right'
                                        multiline
                                        withArrow
                                    >
                                        <IconInfoCircle size={18} />
                                    </Tooltip>
                                </div>
                            </Group>

                            <Group grow>
                                <NumberInput
                                    label='Мин. сумма'
                                    min={0}
                                    placeholder='Введите число'
                                    {...form.getInputProps(`config.paymentChannels.${index}.filter.amount.min.value`)}
                                />
                                <NumberInput
                                    label='Макс. сумма'
                                    min={0}
                                    placeholder='Введите число'
                                    {...form.getInputProps(`config.paymentChannels.${index}.filter.amount.max.value`)}
                                />
                            </Group>

                            <NumberInput
                                label='Макс. длина очереди'
                                placeholder='Введите число'
                                min={0}
                                {...form.getInputProps(`config.paymentChannels.${index}.config.maxQueueLength`)}
                            />
                        </Stack>
                    </Card>
                ))}

                {(!form.values.config?.paymentChannels || form.values.config.paymentChannels.length === 0) && (
                    <Text
                        c='dimmed'
                        size='sm'
                    >
                        Нет добавленных каналов
                    </Text>
                )}

                <Button
                    variant='subtle'
                    leftSection={<IconPlus size={16} />}
                    onClick={addPaymentChannel}
                    size='xs'
                    disabled={isLoading}
                    loading={isLoading}
                >
                    Добавить канал
                </Button>
            </Stack>
        </Fieldset>
    );
};
